// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	createGuildSelectComponents,
	type GuildSelectOption,
} from '@app/features/app/components/dialogs/shared/GuildSelectComponents';
import Guilds from '@app/features/guild/state/Guilds';
import {Select} from '@app/features/ui/components/form/FormSelect';
import styles from '@app/features/user/components/modals/tabs/my_profile_tab/ProfileTypeSelector.module.css';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo} from 'react';

const GLOBAL_PROFILE_DESCRIPTOR = msg({
	message: 'Global profile',
	comment: 'Short label in the profile type selector. Keep it concise.',
});
const PROFILE_TYPE_DESCRIPTOR = msg({
	message: 'Profile type',
	comment: 'Short label in the profile type selector. Keep it concise.',
});

interface ProfileTypeSelectorProps {
	selectedGuildId: string | null;
	onChange: (guildId: string | null) => void;
	disabled: boolean;
}

export const ProfileTypeSelector = observer(({selectedGuildId, onChange, disabled}: ProfileTypeSelectorProps) => {
	const {i18n} = useLingui();
	const guilds = Guilds.getGuilds();
	const guildOptions: Array<GuildSelectOption> = [
		{value: '', label: i18n._(GLOBAL_PROFILE_DESCRIPTOR)},
		...guilds.map((guild) => ({
			value: guild.id,
			label: guild.name || '',
			icon: guild.icon ?? null,
		})),
	];
	const guildSelectComponents = useMemo(
		() =>
			createGuildSelectComponents({
				styles: {
					optionRow: styles.guildOption,
					valueRow: styles.guildValue,
					rowGlobal: styles.guildOptionGlobal,
					avatar: styles.guildAvatar,
					avatarPlaceholder: styles.guildAvatarPlaceholder,
					label: styles.guildOptionLabel,
				},
			}),
		[],
	);
	const renderGuildRow = useCallback((option: GuildSelectOption) => {
		const isGlobal = !option.value;
		const iconUrl = option.icon ? AvatarUtils.getGuildIconURL({id: option.value, icon: option.icon}) : null;
		const initial = option.label.charAt(0).toUpperCase();
		return (
			<div
				className={clsx(styles.guildOption, isGlobal && styles.guildOptionGlobal)}
				data-flx="user.my-profile-tab.profile-type-selector.render-guild-row.guild-option"
			>
				{!isGlobal &&
					(iconUrl ? (
						<div
							className={styles.guildAvatar}
							style={{backgroundImage: `url(${iconUrl})`}}
							aria-hidden
							data-flx="user.my-profile-tab.profile-type-selector.render-guild-row.guild-avatar"
						/>
					) : (
						<div
							className={styles.guildAvatarPlaceholder}
							aria-hidden
							data-flx="user.my-profile-tab.profile-type-selector.render-guild-row.guild-avatar-placeholder"
						>
							{initial}
						</div>
					))}
				<span
					className={styles.guildOptionLabel}
					data-flx="user.my-profile-tab.profile-type-selector.render-guild-row.guild-option-label"
				>
					{option.label}
				</span>
			</div>
		);
	}, []);
	const renderOption = useCallback((option: GuildSelectOption) => renderGuildRow(option), [renderGuildRow]);
	const renderValue = useCallback(
		(option: GuildSelectOption | null) => {
			if (!option) return null;
			return renderGuildRow(option);
		},
		[renderGuildRow],
	);
	return (
		<div className={styles.container} data-flx="user.my-profile-tab.profile-type-selector.container">
			<Select
				label={i18n._(PROFILE_TYPE_DESCRIPTOR)}
				value={selectedGuildId || ''}
				options={guildOptions}
				onChange={(value) => onChange(value === '' ? null : value)}
				disabled={disabled}
				className={clsx(disabled && styles.disabled)}
				components={guildSelectComponents}
				renderOption={renderOption}
				renderValue={renderValue}
				data-flx="user.my-profile-tab.profile-type-selector.disabled.change"
			/>
			{selectedGuildId && (
				<p className={styles.description} data-flx="user.my-profile-tab.profile-type-selector.description">
					<Trans>
						You are editing your per-community profile. This profile will only be visible in this community and will
						override your global profile.
					</Trans>
				</p>
			)}
		</div>
	);
});
